import { AdvancedSelect } from '../../../../widgets/select';
import { ZSwitch } from '../../../../widgets/zswitch';
import React, { FC, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { Model3DParameterValues } from '../../modelGeneration3DParameters';
import { videoGenerationModelsQuery } from '../../queries/__generated__/videoGenerationModelsQuery.graphql';
import { VideoGenerationModelsQuery } from '../../queries/videoGenerationModels';
import { MODEL3D_TOOL_IDS, ModelGeneration3DModel, ToolType } from '../../types';
import { AD_LANGUAGES, AdOptions, VideoOptions } from '../../types';
import { Model3DSettingsSidebar } from '../navigation/model3DSettingsSidebar';

const PanelContainer = styled.div`
    position: relative;
    z-index: 6;
    width: 280px;
    background: #ffffff;
    border-left: 1px solid ${(props) => props.theme.app.borderColor};
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const PanelHeader = styled.div`
    padding: 16px 20px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
`;

const PanelTitle = styled.h3`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const PanelContent = styled.div`
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SettingGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const SettingLabel = styled.label`
    font-size: 13px;
    font-weight: 400;
    color: ${(props) => props.theme.app.textColor};
`;

const settingFieldStyles = `
	padding: 10px 12px;
	border-radius: 8px;
	font-size: 13px;
	font-weight: 500;
	width: 100%;
	box-sizing: border-box;

	&:hover {
		border-color: #3b82f6;
	}

	&:focus {
		outline: none;
		border-color: #3b82f6;
		box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
	}

	&::placeholder {
		color: #9ca3af;
	}
`;

const SettingInput = styled.input`
    ${settingFieldStyles}
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.textColor};
`;

const SettingTextarea = styled.textarea`
    ${settingFieldStyles}
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};
    color: ${(props) => props.theme.app.textColor};
    resize: vertical;
    min-height: 60px;
    font-family: inherit;
`;

const SwitchRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

// --- Model select custom rendering ---

interface SelectOption {
    value: string;
    label: string;
}

interface ModelSelectOption extends SelectOption {
    description: string;
    tooltip: string;
}

const VIDEO_MODEL_META: Record<string, { displayName: string; description: string; tooltip: string }> = {
    'wan-2.5': {
        displayName: 'Wan 2.5',
        description: 'Cinematic clips with built-in sound.',
        tooltip:
            'Generate video and native audio together\u2014dialogue, ambience, and music in one pass. Great when you want a complete, immersive moment without extra post-production.'
    },
    'kling-2.6-pro': {
        displayName: 'Kling 2.6 Pro',
        description: 'Turn a still image into a premium cinematic shot.',
        tooltip:
            'Best for image-to-video when you want strong realism and a polished look from a reference frame. Ideal for product shots, social ads, and hero visuals where consistency matters.'
    },
    'seedance-pro': {
        displayName: 'Seedance Pro',
        description: 'Storyboard-to-screen, cinematic motion made easy.',
        tooltip:
            'Designed for cinematic text-to-video and concept-driven scenes with smooth motion and mood. Great for previz, trailers, and creative exploration before final production.'
    },
    veo3: {
        displayName: 'Veo 3',
        description: 'High-end realism with strong prompt control.',
        tooltip:
            'Built for detailed prompts and better character consistency across multiple generations. Choose it when you need premium, film-like realism for narrative or brand work.'
    },
    'ltx-2-pro': {
        displayName: 'LTX 2 Pro',
        description: 'Professional-grade, polished renders.',
        tooltip:
            'Higher fidelity output for demanding projects and marketing-ready results. Native audio + strong creative control make it a great choice for final takes.'
    },
    'ltx-2-fast': {
        displayName: 'LTX 2 Fast',
        description: 'Fast iterations for rapid creative testing.',
        tooltip:
            'Optimized for speed and lower cost\u2014perfect for previews, prototyping, and short-form content. Use Fast to explore options, then switch to Pro for the final polish.'
    }
};

function getModelMeta(modelId: string) {
    return VIDEO_MODEL_META[modelId] || { displayName: modelId, description: '', tooltip: '' };
}

const ModelOptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const ModelOptionName = styled.span`
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
`;

const ModelOptionDescription = styled.span`
    font-size: 11px;
    opacity: 0.65;
    line-height: 1.3;
`;

const formatModelOption = (option: ModelSelectOption, meta: { context: string }) => (
    <ModelOptionWrapper title={meta.context === 'menu' ? option.tooltip : undefined}>
        <ModelOptionName>{option.label}</ModelOptionName>
        {option.description && <ModelOptionDescription>{option.description}</ModelOptionDescription>}
    </ModelOptionWrapper>
);

const selectStyles = {
    control: (provided: Record<string, unknown>) => ({
        ...provided,
        borderRadius: 8
    })
};

// --- Component ---

interface SettingsPanelProps {
    toolId: ToolType;
    videoOptions?: VideoOptions | null;
    onVideoOptionsChange?: (options: VideoOptions) => void;
    adOptions?: AdOptions;
    onAdOptionsChange?: (options: AdOptions) => void;
    modelGeneration3DModels?: readonly ModelGeneration3DModel[];
    selectedModel3DModel?: string;
    onModel3DModelChange?: (agentId: string, model: string) => void;
    model3DParameterValues?: Model3DParameterValues;
    onModel3DParameterValuesChange?: (agentId: string, model: string, values: Model3DParameterValues) => void;
}

export const SettingsPanel: FC<SettingsPanelProps> = ({
    toolId,
    videoOptions,
    onVideoOptionsChange,
    adOptions,
    onAdOptionsChange,
    modelGeneration3DModels = [],
    selectedModel3DModel,
    onModel3DModelChange,
    model3DParameterValues,
    onModel3DParameterValuesChange
}) => {
    const videoModels = useLazyLoadQuery<videoGenerationModelsQuery>(VideoGenerationModelsQuery, {});

    const modelOptions: ModelSelectOption[] = useMemo(
        () =>
            videoModels.videoGenerationModels.map((m) => {
                const meta = getModelMeta(m.model);
                return {
                    value: m.model,
                    label: meta.displayName,
                    description: meta.description,
                    tooltip: meta.tooltip
                };
            }),
        [videoModels]
    );

    if (MODEL3D_TOOL_IDS.includes(toolId)) {
        return (
            <Model3DSettingsSidebar
                agentId={toolId}
                models={modelGeneration3DModels}
                selectedModel={selectedModel3DModel}
                onModelChange={onModel3DModelChange}
                parameterValues={model3DParameterValues}
                onParameterValuesChange={onModel3DParameterValuesChange}
            />
        );
    }

    if (toolId === 'video-generation' && videoOptions && onVideoOptionsChange) {
        const selectedModel = videoModels.videoGenerationModels.find((m) => m.model === videoOptions.model);
        const hasAspectRatios = selectedModel && selectedModel.allowedAspectRatios.length > 0;
        const hasGenerateAudio = selectedModel?.supportsGenerateAudio ?? false;

        const durationOptions: SelectOption[] =
            selectedModel?.allowedDurations.map((d) => ({
                value: String(d),
                label: `${d} ${T._('seconds', 'Admin')}`
            })) || [];

        const resolutionOptions: SelectOption[] =
            selectedModel?.allowedResolutions.map((r) => ({
                value: r,
                label: r
            })) || [];

        const aspectRatioOptions: SelectOption[] =
            selectedModel?.allowedAspectRatios.map((r) => ({
                value: r,
                label: r
            })) || [];

        return (
            <PanelContainer>
                <PanelHeader>
                    <PanelTitle>{T._('Video Settings', 'Admin')}</PanelTitle>
                </PanelHeader>
                <PanelContent>
                    <SettingGroup>
                        <SettingLabel>{T._('Model', 'Admin')}</SettingLabel>
                        <AdvancedSelect
                            value={modelOptions.find((o) => o.value === videoOptions.model)}
                            onChange={(opt: ModelSelectOption | null) => {
                                const newModel = videoModels.videoGenerationModels.find((m) => m.model === opt?.value);
                                if (newModel) {
                                    onVideoOptionsChange({
                                        ...videoOptions,
                                        model: newModel.model,
                                        duration: newModel.allowedDurations[0],
                                        resolution: newModel.allowedResolutions[0],
                                        aspectRatio: newModel.allowedAspectRatios[0],
                                        generateAudio: false
                                    });
                                }
                            }}
                            options={modelOptions}
                            formatOptionLabel={formatModelOption}
                            isSearchable={false}
                            styles={selectStyles}
                        />
                    </SettingGroup>

                    <SettingGroup>
                        <SettingLabel>{T._('Duration', 'Admin')}</SettingLabel>
                        <AdvancedSelect
                            value={durationOptions.find((o) => o.value === String(videoOptions.duration))}
                            onChange={(opt: SelectOption | null) => opt && onVideoOptionsChange({ ...videoOptions, duration: parseInt(opt.value, 10) })}
                            options={durationOptions}
                            isSearchable={false}
                            styles={selectStyles}
                        />
                    </SettingGroup>

                    <SettingGroup>
                        <SettingLabel>{T._('Resolution', 'Admin')}</SettingLabel>
                        <AdvancedSelect
                            value={resolutionOptions.find((o) => o.value === videoOptions.resolution)}
                            onChange={(opt: SelectOption | null) => opt && onVideoOptionsChange({ ...videoOptions, resolution: opt.value })}
                            options={resolutionOptions}
                            isSearchable={false}
                            styles={selectStyles}
                        />
                    </SettingGroup>

                    {hasAspectRatios && (
                        <SettingGroup>
                            <SettingLabel>{T._('Aspect Ratio', 'Admin')}</SettingLabel>
                            <AdvancedSelect
                                value={aspectRatioOptions.find((o) => o.value === (videoOptions.aspectRatio ?? ''))}
                                onChange={(opt: SelectOption | null) => opt && onVideoOptionsChange({ ...videoOptions, aspectRatio: opt.value })}
                                options={aspectRatioOptions}
                                isSearchable={false}
                                styles={selectStyles}
                            />
                        </SettingGroup>
                    )}

                    {hasGenerateAudio && (
                        <SettingGroup>
                            <SwitchRow>
                                <SettingLabel>{T._('Generate Audio', 'Admin')}</SettingLabel>
                                <ZSwitch
                                    variant='ai'
                                    checked={videoOptions.generateAudio}
                                    onChange={(checked) => onVideoOptionsChange({ ...videoOptions, generateAudio: checked })}
                                />
                            </SwitchRow>
                        </SettingGroup>
                    )}
                </PanelContent>
            </PanelContainer>
        );
    }

    if (toolId === 'ad-generator' && adOptions && onAdOptionsChange) {
        const languageOptions: SelectOption[] = AD_LANGUAGES.map((lang) => ({
            value: lang.code,
            label: lang.label
        }));

        return (
            <PanelContainer>
                <PanelHeader>
                    <PanelTitle>{T._('Ad Settings', 'Admin')}</PanelTitle>
                </PanelHeader>
                <PanelContent>
                    <SettingGroup>
                        <SettingLabel>{T._('Language for the ad copy', 'Admin')}</SettingLabel>
                        <AdvancedSelect
                            value={languageOptions.find((o) => o.value === adOptions.language)}
                            onChange={(opt: SelectOption | null) => opt && onAdOptionsChange({ ...adOptions, language: opt.value })}
                            options={languageOptions}
                            isSearchable={false}
                            styles={selectStyles}
                        />
                    </SettingGroup>

                    <SettingGroup>
                        <SettingLabel>{T._('Title', 'Admin')}</SettingLabel>
                        <SettingInput
                            type='text'
                            value={adOptions.title}
                            onChange={(e) => onAdOptionsChange({ ...adOptions, title: e.target.value })}
                            placeholder={T._('Enter ad title...', 'Admin')}
                            maxLength={100}
                        />
                    </SettingGroup>

                    <SettingGroup>
                        <SettingLabel>{T._('Subtitle', 'Admin')}</SettingLabel>
                        <SettingTextarea
                            value={adOptions.subtitle}
                            onChange={(e) => onAdOptionsChange({ ...adOptions, subtitle: e.target.value })}
                            placeholder={T._('Enter ad subtitle...', 'Admin')}
                            maxLength={150}
                        />
                    </SettingGroup>

                    <SettingGroup>
                        <SettingLabel>{T._('Call to Action', 'Admin')}</SettingLabel>
                        <SettingInput
                            type='text'
                            value={adOptions.ctaText}
                            onChange={(e) => onAdOptionsChange({ ...adOptions, ctaText: e.target.value })}
                            placeholder={T._('e.g., Buy Now, Shop Now...', 'Admin')}
                            maxLength={50}
                        />
                    </SettingGroup>
                </PanelContent>
            </PanelContainer>
        );
    }

    // No settings panel for other tools
    return null;
};

export default SettingsPanel;
